/**
 * API COVERAGE — TransportController (/api/transport): Module 4 Transport, part 1 — vehicle types (T1), a transporter's
 * fleet and GST option (T2), rate card types (T3, T4) and the rate sheet download (FR-TRN-10).
 */
import { api, http } from '@/lib/apiClient';

export type ContractType = 'Local' | 'Outstation' | 'PointToPoint' | 'PerDay';
export const CONTRACT_TYPES: ContractType[] = ['Local', 'Outstation', 'PointToPoint', 'PerDay'];
export const CONTRACT_LABEL: Record<ContractType, string> = { Local: 'Local', Outstation: 'Outstation', PointToPoint: 'Point to point', PerDay: 'Per day' };

export interface VehicleTypeUsage { id: number; fleets: number; cardTypes: number; cards: number; subTours: number; used: boolean }
export interface VehicleType {
  id: number; name: string; typicalModels?: string | null; seats: number; seatsText?: string | null; seatsLabel: string; bagsLarge: number;
  bagsSmall: number; hasCarrier: boolean; bagsLabel: string; isAc?: boolean | null; acLabel: string; sortOrder: number; isActive: boolean;
  usage: VehicleTypeUsage;
}
export interface SaveVehicleType {
  name: string; typicalModels?: string | null; seats: number; seatsText?: string | null; bagsLarge: number; bagsSmall: number; hasCarrier: boolean;
  isAc?: boolean | null; isActive: boolean;
}

export interface TransportGstOption {
  id: number; countryIso2: string; name: string; rate: number; inputCredit: boolean; validFrom: string; validTo?: string | null;
  verifiedBy?: string | null; verifiedAt?: string | null; note?: string | null;
}
export interface FleetRow { vehicleTypeId: number; vehicleType: string; ticked: boolean; count?: number | null; registration?: string | null; driver?: string | null }
export interface FleetGst {
  vendorId: number; vendorCode: string; vendorName: string; isTransporter: boolean; fleet: FleetRow[]; gstOptionId?: number | null;
  ratesIncludeGst: boolean; gstOptions: TransportGstOption[]; gstOptionName?: string | null;
}
export interface SaveFleet { fleet: { vehicleTypeId: number; count?: number | null; registration?: string | null; driver?: string | null }[]; gstOptionId?: number | null }

export interface RegionOption { key: string; label: string; kind: 'State' | 'Destination' }
export interface CardColumn { column: string; required: boolean }
export interface RateCardTypeListItem {
  id: number; name: string; regions: string[]; regionText: string; contractType: ContractType; contractLabel: string; hasSeasons: boolean;
  validFrom: string; validTo: string; vehicleTypes: number; cards: number; status: 'Active' | 'Inactive';
}
export interface RateCardType {
  id: number; name: string; regions: string[]; regionLabels: RegionOption[]; regionText: string; contractType: ContractType; contractLabel: string;
  hasSeasons: boolean; validFrom: string; validTo: string; vehicleTypeIds: number[]; status: 'Active' | 'Inactive'; cards: number; columns: CardColumn[];
}
export interface SaveRateCardType {
  name: string; regions: string[]; contractType: ContractType; hasSeasons: boolean; validFrom: string; validTo: string; vehicleTypeIds: number[];
  status?: 'Active' | 'Inactive' | null;
}

export const VEHICLE_TYPES_KEY = 'vehicle-types';
export const CARD_TYPES_KEY = 'rate-card-types';
export const FLEET_KEY = 'transport-fleet';

const base = '/transport';

async function download(url: string, params: Record<string, unknown> | undefined, fallbackName: string) {
  const res = await http.get(url, { responseType: 'blob', params });
  const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers['content-disposition'] ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = decodeURIComponent(name); a.click();
  URL.revokeObjectURL(href);
}

export const vehicleTypesApi = {
  list: (includeInactive = true) => api.get<VehicleType[]>(`${base}/vehicle-types`, { includeInactive }),
  add: (b: SaveVehicleType) => api.post<VehicleType>(`${base}/vehicle-types`, b),
  update: (id: number, b: SaveVehicleType) => api.put<VehicleType>(`${base}/vehicle-types/${id}`, b),
  remove: (id: number) => api.del<string>(`${base}/vehicle-types/${id}`),
  order: (ids: number[]) => api.post<string>(`${base}/vehicle-types/order`, { ids }),
};

export const fleetApi = {
  get: (vendorId: number) => api.get<FleetGst>(`${base}/vendors/${vendorId}/fleet`),
  save: (vendorId: number, b: SaveFleet) => api.put<FleetGst>(`${base}/vendors/${vendorId}/fleet`, b),
  gstOptions: (country = 'IN') => api.get<TransportGstOption[]>(`${base}/gst-options`, { country }),
};

export const cardTypesApi = {
  list: () => api.get<RateCardTypeListItem[]>(`${base}/card-types`),
  get: (id: number) => api.get<RateCardType>(`${base}/card-types/${id}`),
  regions: () => api.get<RegionOption[]>(`${base}/regions`),
  columns: (type: ContractType) => api.get<CardColumn[]>(`${base}/columns/${type}`),
  add: (b: SaveRateCardType) => api.post<RateCardType>(`${base}/card-types`, b),
  update: (id: number, b: SaveRateCardType) => api.put<RateCardType>(`${base}/card-types/${id}`, b),
  setStatus: (id: number, status: 'Active' | 'Inactive') => api.post<string>(`${base}/card-types/${id}/status`, { status }),
  remove: (id: number) => api.del<string>(`${base}/card-types/${id}`),
  sheet: (id: number, vendorId?: number | null, name = 'rate sheet') =>
    download(`${base}/card-types/${id}/sheet`, vendorId ? { vendorId } : undefined, `${name}.xlsx`),
};

// ---------------------------------------------------------------- Part 2: rate cards (T5–T10), mapping (T13)

export type CardStatus = 'Draft' | 'PendingApproval' | 'Active' | 'Rejected' | 'Expired' | 'ExpiresSoon' | 'Replaced';
export const CARD_STATUS_LABEL: Record<CardStatus, string> = {
  Draft: 'Draft', PendingApproval: 'Pending approval', Active: 'Active', Rejected: 'Rejected', Expired: 'Expired', ExpiresSoon: 'Expires soon', Replaced: 'Replaced',
};
export interface NamedRef { id: number; code: string; name: string }
export interface DateRange { from: string; to: string }
export interface Slab { daysFrom: number; daysTo?: number | null; chargePct: number; isNoShow: boolean }

export interface RateCardListItem {
  id: number; code: string; cardTypeId: number; cardTypeName: string; contractType: ContractType; contractLabel: string; regions: string[];
  vendorId: number; vendorCode: string; vendorName: string; validFrom: string; validTo: string; status: CardStatus; daysLeft?: number | null;
  source: string; replacesCode?: string | null;
}
export interface RateCardList { items: RateCardListItem[]; regions: RegionOption[]; transporters: NamedRef[]; expiringSoon: number; pending: number }
export interface RateCardListParams { search?: string; region?: string; contractType?: string; vendorId?: number; status?: string }

export interface CardSeason { id: number; name: string; ranges: DateRange[] }
export interface CardRate { id: number; seasonId?: number | null; vehicleTypeId: number; vehicleName: string; rate: number; kmPerDay?: number | null; hoursPerDay?: number | null; extraKm: number; extraHour: number }
export interface P2pRate { id: number; seasonId?: number | null; routeId: number; routeText: string; direction: 'OneWay' | 'Return'; vehicleTypeId: number; price: number }
export interface MinDays { id: number; fromDestinationId: number; fromName: string; toDestinationId: number; toName: string; minDays: number }
export interface CardCharge { id: number; name: string; fixedKind?: string | null; included: boolean; amount?: number | null; counted?: 'PerDay' | 'PerTrip' | null }
export interface CardExample { title: string; lines: { item: string; amount: string }[]; note: string }
export interface RateCard {
  id: number; code: string; cardTypeId: number; cardTypeName: string; contractType: ContractType; contractLabel: string; hasSeasons: boolean;
  typeValidFrom: string; typeValidTo: string; regionText: string; vendorId: number; vendorCode: string; vendorName: string; gstOptionName?: string | null;
  gstRate?: number | null; validFrom: string; validTo: string; status: CardStatus; shownStatus: CardStatus; source: string; copiedFromCode?: string | null;
  replacesId?: number | null; replacesCode?: string | null; reviewNote?: string | null; submittedAt?: string | null; approvedAt?: string | null;
  packageHours?: number | null; packageKm?: number | null; vehicles: { id: number; name: string }[]; seasons: CardSeason[]; rates: CardRate[]; p2p: P2pRate[];
  minDays: MinDays[]; charges: CardCharge[]; slabs: Slab[]; seasonGaps: string[]; example?: CardExample | null; missingForApproval: string[]; needsReview: boolean;
}
export interface SaveRateCard {
  cardTypeId: number; vendorId: number; validFrom: string; validTo: string; packageHours?: number | null; packageKm?: number | null;
  seasons?: { key: string; name: string; ranges: DateRange[] }[] | null;
  rates?: { seasonKey?: string | null; vehicleTypeId: number; rate?: number | null; kmPerDay?: number | null; hoursPerDay?: number | null; extraKm?: number | null; extraHour?: number | null }[] | null;
  p2p?: { seasonKey?: string | null; routeId: number; direction: string; vehicleTypeId: number; price?: number | null }[] | null;
  minDays?: { fromDestinationId: number; toDestinationId: number; minDays?: number | null }[] | null;
  charges?: { name: string; fixedKind?: string | null; included: boolean; amount?: number | null; counted?: string | null }[] | null;
  slabs?: Slab[] | null;
}
export interface CopyCard { validFrom: string; validTo: string; upliftPct: number; roundTo: number; moveDatesByYear: boolean; replaces: boolean }

export interface MappingRow {
  id: number; region: string; regionName: string; startDestinationId: number; startTransferPointId?: number | null; startIsLocal: boolean; startLabel: string;
  rateCardId?: number | null; cardText?: string | null; vendorId?: number | null; use: 'Preferred' | 'Backup'; useShown: 'Preferred' | 'Backup' | 'Not mapped';
  cardValidTill?: string | null; expiresSoon: boolean; cardUsable: boolean; expired: boolean;
}
/** M4.7: the card the auto quote uses for a region and starting point. */
export interface TransportPick {
  cardId?: number | null; code?: string | null; vendorName?: string | null; source: 'Preferred' | 'Backup' | 'Cheapest'; notMapped: boolean; message: string;
}
export interface StartOption { key: string; destinationId: number; transferPointId?: number | null; isLocal: boolean; label: string }
export interface MappingList { rows: MappingRow[]; regions: RegionOption[]; starts: StartOption[]; transporters: NamedRef[]; expiringSoon: number }
/** `covers`: every region / destination key the card can be mapped to. */
export interface MappingCardOption { id: number; text: string; vendorId: number; regions: string[]; validTill: string; contractType: ContractType; covers: string[] }
export interface MappingOptions { regions: RegionOption[]; starts: StartOption[]; cards: MappingCardOption[] }
export interface SaveMapping {
  region: string; startDestinationId: number; startTransferPointId?: number | null; startIsLocal: boolean; rateCardId?: number | null; use: 'Preferred' | 'Backup';
}

export const RATE_CARDS_KEY = 'transport-rate-cards';
export const MAPPINGS_KEY = 'transport-mappings';

export const rateCardsApi = {
  list: (p: RateCardListParams) => api.get<RateCardList>(`${base}/rate-cards`, p as Record<string, unknown>),
  get: (id: number) => api.get<RateCard>(`${base}/rate-cards/${id}`),
  blank: (typeId: number, vendorId: number) => api.get<RateCard>(`${base}/rate-cards/new`, { typeId, vendorId }),
  add: (b: SaveRateCard) => api.post<RateCard>(`${base}/rate-cards`, b),
  update: (id: number, b: SaveRateCard) => api.put<RateCard>(`${base}/rate-cards/${id}`, b),
  send: (id: number) => api.post<RateCard>(`${base}/rate-cards/${id}/send`),
  approve: (id: number) => api.post<RateCard>(`${base}/rate-cards/${id}/approve`),
  reject: (id: number, reason: string) => api.post<RateCard>(`${base}/rate-cards/${id}/reject`, { reason }),
  revise: (id: number) => api.post<RateCard>(`${base}/rate-cards/${id}/revise`),
  copy: (id: number, b: CopyCard) => api.post<RateCard>(`${base}/rate-cards/${id}/copy`, b),
  remove: (id: number) => api.del<string>(`${base}/rate-cards/${id}`),
};

export const mappingsApi = {
  list: (p: { search?: string; region?: string; start?: string; vendorId?: number; use?: string }) => api.get<MappingList>(`${base}/mappings`, p as Record<string, unknown>),
  options: () => api.get<MappingOptions>(`${base}/mappings/options`),
  add: (b: SaveMapping) => api.post<MappingRow>(`${base}/mappings`, b),
  update: (id: number, b: SaveMapping) => api.put<MappingRow>(`${base}/mappings/${id}`, b),
  remove: (id: number) => api.del<string>(`${base}/mappings/${id}`),
  resolve: (p: { region: string; startId: number; pointId?: number | null; local?: boolean; on?: string | null }) =>
    api.get<TransportPick>(`${base}/mappings/resolve`, p as Record<string, unknown>),
};

/** "₹1,400" — Indian grouping. */
export const rs = (v?: number | null) => (v == null ? '—' : `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

/** "1 Apr 2026" */
export const day = (iso?: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—';

// ---------------------------------------------------------------- Part 3: upload sheet (T11), review (T12), calculator (T14)

export type CardSource = 'Manual' | 'Sheet' | 'Ai' | 'Portal';
export const SOURCE_LABEL: Record<CardSource, string> = { Manual: 'Staff', Sheet: 'Rate sheet', Ai: 'Read by AI', Portal: 'Vendor portal' };

/** `resolved`: fixed since the upload (found by a re-check); shown struck through and not counted. */
export interface SheetCheckRow { row: number; item: string; problem: string; level: 'Error' | 'Check'; resolved?: boolean }
/**
 * `sheetDownloadedOn`: when the TravelERP sheet was downloaded (absent for an AI read or a foreign file);
 * `correctsCode` / `correctionNote`: the draft sent back for correction that this upload replaces, and why.
 */
export interface SheetCheck {
  uploadId: number; cardTypeId: number; cardTypeName: string; contractLabel: string; vendorId: number; vendorCode: string; vendorName: string; fileName: string;
  source: CardSource; status: 'Checked' | 'Submitted' | 'Draft'; rateCardId?: number | null; rateCardCode?: string | null; replacesCode?: string | null;
  rows: SheetCheckRow[]; errors: number; checks: number; rowsRead: number; createdAt: string;
  sheetDownloadedOn?: string | null; correctsCode?: string | null; correctionNote?: string | null;
}
export interface SheetSubmit { rateCardId: number; code: string; message: string }

export const SHEETS_KEY = 'transport-sheets';
export const sheetsApi = {
  check: (b: { cardTypeId: number; vendorId: number; fileId: number; useAi: boolean }) => api.post<SheetCheck>(`${base}/sheets/check`, b),
  list: (vendorId?: number) => api.get<SheetCheck[]>(`${base}/sheets`, vendorId ? { vendorId } : undefined),
  get: (id: number) => api.get<SheetCheck>(`${base}/sheets/${id}`),
  submit: (id: number) => api.post<SheetSubmit>(`${base}/sheets/${id}/submit`),
  correct: (id: number) => api.post<SheetSubmit>(`${base}/sheets/${id}/correct`),
  recheck: (id: number) => api.post<SheetCheck>(`${base}/sheets/${id}/recheck`),
};

/** "28 Sep" — the day a rate sheet was downloaded (T11 subtitle). */
export const shortDay = (iso?: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';

export interface ReviewCell { field: string; label: string; value?: string | null; was?: string | null; input: 'number' | 'date' | 'yesno' | 'counted' | 'none'; editedFrom?: string | null }
export interface ReviewRow { key: string; section: string; label: string; cells: ReviewCell[]; ticked: boolean; isNew: boolean; changed: boolean }
export interface ReviewEdit { key: string; label: string; old?: string | null; new?: string | null }
export interface TransportReview {
  cardId: number; code: string; cardTypeName: string; contractType: ContractType; contractLabel: string; regionText: string; vendorId: number; vendorCode: string;
  vendorName: string; source: CardSource; status: CardStatus; validFrom: string; validTo: string; comparedWithCode?: string | null; fileId?: number | null;
  fileName?: string | null; submittedAt?: string | null; sections: string[]; rows: ReviewRow[]; ticked: number; total: number; edits: ReviewEdit[];
  missing: string[]; checks: SheetCheckRow[];
}
export interface TransportApproval {
  id: number; code: string; title: string; subtitle: string; submittedAt?: string | null; source: CardSource; fromPortal: boolean; stepByStep: boolean; ticked: number; total: number;
}
export type ReviewDecision = 'Approve' | 'Correction' | 'Reject';

export const TRANSPORT_APPROVALS_KEY = 'transport-approvals';
export const reviewApi = {
  approvals: () => api.get<TransportApproval[]>(`${base}/approvals`),
  get: (cardId: number) => api.get<TransportReview>(`${base}/reviews/${cardId}`),
  save: (cardId: number, b: { ticked?: string[]; edits?: { key: string; field: string; value: string | null }[] }) => api.put<TransportReview>(`${base}/reviews/${cardId}`, b),
  decide: (cardId: number, decision: ReviewDecision, reason?: string | null) => api.post<string>(`${base}/reviews/${cardId}/decide`, { decision, reason }),
};

export interface CalcDayIn { fromDestinationId?: number | null; toDestinationId?: number | null; distanceKm?: number | null; local: boolean }
export interface CalcDay { day: number; date: string; fromDestinationId?: number | null; fromName: string; toDestinationId?: number | null; toName: string; distanceKm: number; local: boolean; note?: string | null; minutes?: number | null }
export interface CalcVehicle { id: number; name: string; seats: number; seatsText: string; bagsLarge: number; bagsSmall: number; fits: boolean; suggested: boolean }
export interface CalcResult {
  cardId: number; code: string; cardTypeName: string; contractType: ContractType; contractLabel: string; vendorId: number; vendorName: string; used: boolean;
  notUsedReason?: string | null; working: string; cost: number; gstName?: string | null; gstRate: number; gst: number; total: number; kmLimit?: number | null;
  hoursLimit?: number | null; termsLine?: string | null; cheapest: boolean; extraKm?: number | null; extraHour?: number | null;
}
export interface CalcOut {
  days: CalcDay[]; tripKm: number; pax: number; largeBags: number; vehicles: CalcVehicle[]; suggestedVehicleId?: number | null; vehicleTypeId: number; vehicleName: string;
  overrideUsed: boolean; results: CalcResult[]; terms: string[]; notes: string[]; runId: number; vehicleCount: number; suggestedCount: number;
}
export interface CalcIn {
  startDestinationId: number; startDate: string; days: CalcDayIn[]; adults: number; children: number; largeBags: number; vehicleTypeId?: number | null; overrideReason?: string | null;
  vehicleCount?: number | null;
}

export const calcApi = {
  options: () => api.get<{ destinations: NamedRef[]; vehicles: CalcVehicle[] }>(`${base}/calculator/options`),
  run: (b: CalcIn) => api.post<CalcOut>(`${base}/calculator`, b),
};
